import React from 'react';
import BaseModal from '../../../utils/BaseModal';
import {NewsletterCampaign} from './Newsletter';
import {CalendarToday, CheckCircleOutline, ErrorOutline, People} from '@mui/icons-material';

type Props = {
  open: boolean;
  campaign: NewsletterCampaign | null;
  onClose: () => void;
};

const NewsletterInfoModal: React.FC<Props> = ({ open, campaign, onClose }) => {
  if (!campaign) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENDING': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'COMPLETED': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      default: return 'bg-neutral-500/10 text-neutral-500 border-neutral-500/20';
    }
  };

  const StatBox = ({ icon: Icon, label, value, colorClass }: any) => (
      <div className="flex-1 bg-neutral-50 dark:bg-white/5 border border-neutral-100 dark:border-white/5 p-3 rounded-lg">
        <div className="flex items-center gap-2 mb-1">
          <Icon sx={{ fontSize: 14 }} className="text-neutral-500" />
          <span className="text-[10px] font-black uppercase text-neutral-500 tracking-tighter">{label}</span>
        </div>
        <div className={`text-lg font-bold ${colorClass || 'dark:text-white'}`}>{value}</div>
      </div>
  );

  return (
      <BaseModal
          open={open}
          title="Campaign Details"
          onAbort={onClose}
          maxWidth="md"
      >
        <div className="flex flex-col gap-6">
          {/* Header Info */}
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold dark:text-white leading-none mb-1">{campaign.subject}</h3>
                <div className="flex items-center gap-2 text-neutral-500 text-xs">
                  <CalendarToday sx={{ fontSize: 12 }} />
                  {new Date(campaign.creationDate).toLocaleString('de-DE')}
                  <span className="text-neutral-700">|</span>
                  <span>ID: #{campaign.id}</span>
                </div>
              </div>
              <div className={`px-3 py-1 rounded text-[10px] font-black border uppercase ${getStatusColor(campaign.status)}`}>
                {campaign.status}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="flex gap-4">
            <StatBox
                icon={People}
                label="Total Recipients"
                value={campaign.totalRecipients}
            />
            <StatBox
                icon={CheckCircleOutline}
                label="Successfully Sent"
                value={campaign.sentCount}
                colorClass="text-emerald-500"
            />
            <StatBox
                icon={ErrorOutline}
                label="Failed"
                value={campaign.failedCount}
                colorClass="text-red-500"
            />
          </div>

          {/* Preview Section */}
          <div className="flex flex-col gap-2">
            <span className="text-[10px] font-black uppercase text-neutral-500 tracking-widest">Content Preview</span>
            <div className="border border-neutral-100 dark:border-white/5 rounded-xl overflow-hidden bg-white">
              {/* Iframe wird genutzt, um das Email-Styling vom Website-Styling zu isolieren */}
              <iframe
                  title="Newsletter Preview"
                  srcDoc={`
                <html>
                  <head>
                    <style>
                      body { font-family: sans-serif; margin: 0; padding: 20px; color: #333; }
                      img { max-width: 100%; height: auto; }
                    </style>
                  </head>
                  <body>${campaign.content}</body>
                </html>
              `}
                  className="w-full h-[400px] border-none"
              />
            </div>
          </div>
        </div>
      </BaseModal>
  );
};

export default NewsletterInfoModal;